import {
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Select,
  FormControl,
  MenuItem,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TablePagination,
} from '@mui/material';
import { FileDownloadRounded, PictureAsPdfRounded } from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTenantStore } from '../../../../stores/tenant.store';
import { type AcademicRow, type FinancialRow } from '../../../../hooks/use-reports';
import { formatDate } from '../../../../lib/i18n-format';

export interface ExportColumn {
  /** Su texto es `reports.exportTab.columns.<id>` (en el idioma de la interfaz,
   *  también en los encabezados del Excel y del PDF). */
  id: string;
  getValue: (academic: AcademicRow, financial?: FinancialRow) => string | number | boolean;
}

const EXPORT_COLUMNS: ExportColumn[] = [
  { id: 'name', getValue: (a) => a.name },
  { id: 'email', getValue: (a) => a.email },
  { id: 'personType', getValue: (a) => a.personType },
  { id: 'program', getValue: (a) => a.programName },
  { id: 'enrolled', getValue: (a) => a.enrolledSections },
  { id: 'plan', getValue: (a) => a.planType },
  { id: 'exempt', getValue: (a) => a.isExempt },
  { id: 'total', getValue: (_, f) => f?.totalAmount ?? 0 },
  { id: 'paid', getValue: (_, f) => f?.paidAmount ?? 0 },
  { id: 'pending', getValue: (_, f) => f?.pendingAmount ?? 0 },
  // El saldo de las cuotas vencidas (regla de `installmentState`), no su monto.
  { id: 'overdue', getValue: (_, f) => f?.overdueAmount ?? 0 },
];

export function ExportTab({
  academicRows,
  financialRows,
  filteredAcademicRows,
  filteredFinancialRows,
  isAcadFilterActive,
  isFinFilterActive,
}: {
  academicRows: AcademicRow[];
  financialRows: FinancialRow[];
  filteredAcademicRows: AcademicRow[];
  filteredFinancialRows: FinancialRow[];
  isAcadFilterActive: boolean;
  isFinFilterActive: boolean;
}) {
  const { t } = useTranslation('sis');
  const x = (key: string, o?: Record<string, unknown>) => t(`reports.exportTab.${key}`, o ?? {});
  const tenantTheme = useTenantStore((s) => s.currentTenantTheme);
  const tenantName = tenantTheme?.name?.toUpperCase() || x('pdfTitle').toUpperCase();
  const tenantFileName = tenantTheme?.name?.replace(/\s+/g, '_') || x('fileTenant');
  const columns = EXPORT_COLUMNS.map((c) => ({
    ...c,
    label: x(`columns.${c.id}`),
    value: (a: AcademicRow, f?: FinancialRow) => {
      const v = c.getValue(a, f);
      return typeof v === 'boolean' ? x(v ? 'yes' : 'no') : v;
    },
  }));

  const [selectedColumns, setSelectedColumns] = useState<string[]>(EXPORT_COLUMNS.map((c) => c.id));
  const [filterEnrolled, setFilterEnrolled] = useState<'all' | 'enrolled' | 'none'>('all');
  const [filterPlan, setFilterPlan] = useState<'all' | 'active' | 'none'>('all');
  const [filterDebt, setFilterDebt] = useState<'all' | 'debt' | 'overdue'>('all');
  const [filterProgram, setFilterProgram] = useState<string>('all');
  const [filterCourse, setFilterCourse] = useState<string>('all');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleColumn = (id: string) => {
    setSelectedColumns((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const finMap = useMemo(
    () => new Map<string, FinancialRow>(financialRows.map((f) => [f.studentId, f])),
    [financialRows],
  );

  const uniquePrograms = useMemo(() => {
    const set = new Set<string>();
    academicRows.forEach((r) => {
      if (r.programName && r.programName !== '—') set.add(r.programName);
    });
    return Array.from(set).sort();
  }, [academicRows]);

  const uniqueCourses = useMemo(() => {
    const set = new Set<string>();
    academicRows.forEach((r) => {
      r.enrolledCourses?.forEach((c) => {
        if (c.courseName && c.courseName !== '—') set.add(c.courseName);
      });
    });
    return Array.from(set).sort();
  }, [academicRows]);

  const exportData = useMemo(() => {
    let rows = [...academicRows];

    // Apply global tab filters if active
    if (isAcadFilterActive) {
      const acadSet = new Set(filteredAcademicRows.map((r) => r.personId));
      rows = rows.filter((r) => acadSet.has(r.personId));
    }

    if (isFinFilterActive) {
      const finSet = new Set(filteredFinancialRows.map((r) => r.studentId));
      rows = rows.filter((r) => finSet.has(r.personId));
    }

    // Apply filters
    if (filterEnrolled === 'enrolled') {
      rows = rows.filter((r) => r.enrolledSections > 0);
    } else if (filterEnrolled === 'none') {
      rows = rows.filter((r) => r.enrolledSections === 0);
    }

    if (filterProgram !== 'all') {
      rows = rows.filter((r) => r.programName === filterProgram);
    }

    if (filterCourse !== 'all') {
      rows = rows.filter((r) => r.enrolledCourses?.some((c) => c.courseName === filterCourse));
    }

    if (filterPlan === 'active') {
      rows = rows.filter((r) => r.hasActivePlan);
    } else if (filterPlan === 'none') {
      rows = rows.filter((r) => !r.hasActivePlan);
    }

    if (filterDebt === 'debt') {
      rows = rows.filter((r) => (finMap.get(r.personId)?.pendingAmount ?? 0) > 0);
    } else if (filterDebt === 'overdue') {
      rows = rows.filter((r) => (finMap.get(r.personId)?.overdueAmount ?? 0) > 0);
    }

    return rows.map((ac) => {
      const fin = finMap.get(ac.personId);
      const rowData: Record<string, string | number> = {};
      columns.forEach((col) => {
        if (selectedColumns.includes(col.id)) {
          rowData[col.label] = col.value(ac, fin);
        }
      });
      return rowData;
    });
  }, [
    academicRows,
    finMap,
    filterEnrolled,
    filterPlan,
    filterDebt,
    filterProgram,
    filterCourse,
    selectedColumns,
  ]);

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(exportData);
    // Autowidth simple adjustment
    const cols = columns
      .filter((c) => selectedColumns.includes(c.id))
      .map((col) => {
        const w = Math.max(
          col.label.length,
          ...exportData.map((r) => String(r[col.label] ?? '').length),
        );
        return { wch: Math.min(w + 2, 50) };
      });
    ws['!cols'] = cols;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, x('sheet'));
    XLSX.writeFile(
      wb,
      `${x('fileList')}_${tenantFileName}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');

    doc.setFontSize(16);
    doc.text(tenantName, 14, 20);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(x('pdfSubtitle', { date: formatDate(new Date()) }), 14, 28);

    const shown = columns.filter((c) => selectedColumns.includes(c.id));
    const head = [shown.map((c) => c.label)];
    const body = exportData.map((row) => shown.map((col) => String(row[col.label] ?? '')));

    autoTable(doc, {
      startY: 35,
      head: head,
      body: body,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 8 },
    });

    doc.save(`${x('fileReport')}_${tenantFileName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <Stack spacing={4} sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {x('filtersTitle')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
          <FormControl size="small" fullWidth>
            <Select
              value={filterEnrolled}
              onChange={(e) => setFilterEnrolled(e.target.value as any)}
            >
              <MenuItem value="all">{x('enrolled.all')}</MenuItem>
              <MenuItem value="enrolled">{x('enrolled.with')}</MenuItem>
              <MenuItem value="none">{x('enrolled.without')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={uniquePrograms.length === 0}>
            <Select
              value={filterProgram}
              onChange={(e) => setFilterProgram(e.target.value as string)}
            >
              <MenuItem value="all">{x('programAll')}</MenuItem>
              {uniquePrograms.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={uniqueCourses.length === 0}>
            <Select
              value={filterCourse}
              onChange={(e) => setFilterCourse(e.target.value as string)}
            >
              <MenuItem value="all">{x('courseAll')}</MenuItem>
              {uniqueCourses.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} mt={2}>
          <FormControl size="small" fullWidth>
            <Select value={filterPlan} onChange={(e) => setFilterPlan(e.target.value as any)}>
              <MenuItem value="all">{x('plan.all')}</MenuItem>
              <MenuItem value="active">{x('plan.active')}</MenuItem>
              <MenuItem value="none">{x('plan.none')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <Select value={filterDebt} onChange={(e) => setFilterDebt(e.target.value as any)}>
              <MenuItem value="all">{x('debt.all')}</MenuItem>
              <MenuItem value="debt">{x('debt.pending')}</MenuItem>
              <MenuItem value="overdue">{x('debt.overdue')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {x('columnsTitle')}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 1,
          }}
        >
          {columns.map((col) => (
            <FormControlLabel
              key={col.id}
              control={
                <Checkbox
                  checked={selectedColumns.includes(col.id)}
                  onChange={() => handleToggleColumn(col.id)}
                  size="small"
                />
              }
              label={<Typography variant="body2">{col.label}</Typography>}
            />
          ))}
        </FormGroup>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns
                  .filter((c) => selectedColumns.includes(c.id))
                  .map((col) => (
                    <TableCell key={col.id} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {col.label}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {exportData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={selectedColumns.length}
                    align="center"
                    sx={{ py: 4, color: 'text.secondary' }}
                  >
                    {x('empty')}
                  </TableCell>
                </TableRow>
              ) : (
                exportData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, idx) => (
                    <TableRow key={idx} hover>
                      {columns
                        .filter((c) => selectedColumns.includes(c.id))
                        .map((col) => (
                          <TableCell key={col.id} sx={{ whiteSpace: 'nowrap' }}>
                            {row[col.label] ?? '—'}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={exportData.length}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage={x('rowsPerPage')}
        />
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {x('ready')} <b>{exportData.length}</b>
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfRounded />}
            onClick={handleExportPDF}
            disabled={exportData.length === 0 || selectedColumns.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
          >
            {x('pdfButton')}
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadRounded />}
            onClick={handleExport}
            disabled={exportData.length === 0 || selectedColumns.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
          >
            {x('excelButton')}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
